/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/Charts.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { I } from './ui';
import { chf, date, monthName } from '../lib/format';
import '../styles/insights.css';

/**
 * Kontor charts. One method for all of them (dataviz skill): fixed categorical order (series-1, then
 * series-2), one y-axis, bars ≤24px with a 4px rounded data end and a square baseline, 2px lines,
 * ≥8px markers with a 2px surface ring, recessive grid, a legend for two or more series, a hover
 * layer on every plot, and a table twin. Text always wears text tokens, never the series colour.
 * Charts render at their real pixel width, so 11px axis text stays 11px on every screen.
 */

// ------------------------------------------------------------------ helpers

/** Measured width of a container; re-measured on resize. */
function useWidth(fallback = 720) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { const x = Math.round(e.contentRect.width); if (x > 0) setW(x); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** A readable scale: 3–6 round ticks spanning [lo, hi]. */
export function niceScale(lo: number, hi: number, n = 4) {
  if (lo === hi) hi = lo + 100;
  const raw = (hi - lo) / n;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * p).find((s) => s >= raw) ?? p * 10;
  const a = Math.floor(lo / step) * step, b = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = a; v <= b + step / 2; v += step) ticks.push(Math.round(v));
  return { lo: a, hi: b, step, ticks };
}

/** Compact CHF for axes and direct labels: 120k, 1.2 Mio. (input in Rappen). */
export function kchf(r: number) {
  const v = r / 100, a = Math.abs(v), s = v < 0 ? '−' : '';
  if (a >= 1e6) return `${s}${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)} Mio.`;
  if (a >= 1e3) return `${s}${Math.round(a / 1e3)}k`;
  return `${s}${Math.round(a)}`;
}

/** Column with a 4px rounded data end and a square baseline; works above and below zero. */
function colPath(x: number, w: number, y0: number, y1: number) {
  const h = Math.abs(y1 - y0);
  if (h < 0.5) return '';
  const r = Math.min(4, h, w / 2);
  if (y1 <= y0) return `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 + r} V${y0} Z`;
  return `M${x},${y0} V${y1 - r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 - r} V${y0} Z`;
}

const ms = (d: string) => Date.parse(`${d}T00:00:00Z`);

function ViewToggle({ table, setTable }: { table: boolean; setTable: (b: boolean) => void }) {
  return (
    <div className="segmented" role="group" aria-label="Darstellung">
      <button aria-pressed={!table} onClick={() => setTable(false)}>Diagramm</button>
      <button aria-pressed={table} onClick={() => setTable(true)}>Tabelle</button>
    </div>
  );
}

export function Legend({ items }: { items: { label: string; swatch: 'bar' | 'line' | 'dash' | 'band' | 'tick'; color: string }[] }) {
  return (
    <div className="legend">
      {items.map((x) => (
        <span key={x.label}>
          <svg width="16" height="10" aria-hidden className="swatch">
            {x.swatch === 'bar' && <rect x="3" y="0" width="10" height="10" rx="3" fill={x.color} />}
            {x.swatch === 'line' && <line x1="0" x2="16" y1="5" y2="5" stroke={x.color} strokeWidth="2" strokeLinecap="round" />}
            {x.swatch === 'dash' && <line x1="0" x2="16" y1="5" y2="5" stroke={x.color} strokeWidth="2" strokeDasharray="4 3" />}
            {x.swatch === 'band' && <rect x="0" y="1" width="16" height="8" rx="2" fill={x.color} opacity="0.16" />}
            {x.swatch === 'tick' && <line x1="8" x2="8" y1="0" y2="10" stroke={x.color} strokeWidth="2" />}
          </svg>
          {x.label}
        </span>
      ))}
    </div>
  );
}

function Tip({ x, y, w, children }: { x: number; y: number; w: number; children: ReactNode }) {
  // Flip to the left of the pointer near the right edge, so the tooltip never leaves the card.
  const right = x > w - 180;
  return <div className="tooltip chart-tip" style={{ left: x, top: y, transform: `translate(${right ? 'calc(-100% - 12px)' : '12px'}, -50%)` }}>{children}</div>;
}

const Row = ({ label, value, color, dash }: { label: string; value: ReactNode; color?: string; dash?: boolean }) => (
  <div className="tip-row">
    {color ? <svg width="12" height="8" aria-hidden><line x1="0" x2="12" y1="4" y2="4" stroke={color} strokeWidth="2" strokeDasharray={dash ? '3 2' : undefined} /></svg> : <span style={{ width: 12 }} />}
    <span>{label}</span><b className="num">{value}</b>
  </div>
);

// ------------------------------------------------------------------ Sparkline

/** Trend at a glance for a stat tile; the tile itself carries the number. */
export function Sparkline({ values, label, width = 104, height = 30 }: { values: number[]; label: string; width?: number; height?: number }) {
  if (values.length < 2) return null;
  const lo = Math.min(...values), hi = Math.max(...values);
  const x = (i: number) => 2 + (i * (width - 6)) / (values.length - 1);
  const y = (v: number) => 3 + (height - 6) * (1 - (hi === lo ? 0.5 : (v - lo) / (hi - lo)));
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const zero = lo < 0 && hi > 0 ? y(0) : null;
  return (
    <svg className="spark" width={width} height={height} role="img" aria-label={`${label}: Verlauf über ${values.length} Monate`}>
      {zero != null && <line x1="0" x2={width} y1={zero} y2={zero} className="spark-zero" />}
      <path d={`${d} L${x(values.length - 1)},${height} L${x(0)},${height} Z`} className="spark-area" />
      <path d={d} className="spark-line" />
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="3" className="spark-dot" />
    </svg>
  );
}

// ------------------------------------------------------------------ Delta

/** Change against the comparison period; coloured by whether it is good, and always with a word. */
export function Delta({ value, prior, unit, better = 'up', vs = 'Vorjahr' }: { value: number | null; prior: number | null; unit: 'chf' | 'pct' | 'days' | 'x'; better?: 'up' | 'down'; vs?: string }) {
  if (value == null || prior == null) return <span className="delta dim">kein Vergleich</span>;
  let text: string, change: number;
  if (unit === 'pct') { change = (value - prior) * 100; text = `${change >= 0 ? '+' : '−'}${Math.abs(change).toFixed(1)} Pp.`; }
  else if (unit === 'days') { change = value - prior; text = `${change >= 0 ? '+' : '−'}${Math.abs(change)} Tage`; }
  else if (prior <= 0 || value < 0 || Math.abs(value - prior) > Math.abs(prior) * 2) {
    // A percentage across zero (profit to loss) or of a tiny base says nothing: show the difference in CHF.
    change = value - prior;
    text = `${change >= 0 ? '+' : '−'}CHF ${kchf(Math.abs(change))}`;
  } else {
    change = ((value - prior) / Math.abs(prior)) * 100;
    text = `${change >= 0 ? '+' : '−'}${Math.abs(change).toFixed(1)} %`;
  }
  const flat = unit === 'chf' && Math.abs(value - prior) < 100 ? true : Math.abs(change) < 0.5;
  const good = flat ? null : (change > 0) === (better === 'up');
  return (
    <span className={`delta ${good == null ? 'dim' : good ? 'pos' : 'neg'}`} title={`Vergleich: ${vs}`}>
      <I icon={flat ? Minus : change > 0 ? ArrowUpRight : ArrowDownRight} sm />
      {text}<span className="vs"> ggü. {vs}</span>
    </span>
  );
}

// ------------------------------------------------------------------ Score ring

export function ScoreRing({ score, grade, status, size = 136 }: { score: number; grade: string; status: 'good' | 'watch' | 'bad'; size?: number }) {
  const r = size / 2 - 9, c = 2 * Math.PI * r;
  const [shown, setShown] = useState(0);
  useLayoutEffect(() => { const f = requestAnimationFrame(() => setShown(score)); return () => cancelAnimationFrame(f); }, [score]);
  return (
    <div className="score-ring sized" style={{ width: size, height: size }} role="img" aria-label={`Gesundheitswert ${score} von 100: ${grade}`}>
      <svg width={size} height={size} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} className="track" />
        <circle cx={size / 2} cy={size / 2} r={r} className={`arc ${status}`} strokeDasharray={c} strokeDashoffset={c * (1 - shown / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="score-center"><b className="num">{score}</b><span>{grade}</span></div>
    </div>
  );
}

/** A 0–100 meter with the status in words beside it. */
export function Meter({ score, status }: { score: number; status: 'good' | 'watch' | 'bad' }) {
  return <span className={`meter ${status}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={score}><i style={{ width: `${Math.max(2, score)}%` }} /></span>;
}

// ------------------------------------------------------------------ Cash: history + forecast band

export interface CashPoint { d: string; v: number }
export interface ForecastPoint { d: string; expected: number; low: number; high: number }

export function CashChart({ actual, forecast, today, minimum, height = 280 }: { actual: CashPoint[]; forecast: ForecastPoint[]; today: string; minimum?: { date: string; expected: number } | null; height?: number }) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const pad = { l: 60, r: 16, t: 20, b: 30 };

  const pts = useMemo(() => {
    const m = new Map<string, { d: string; v?: number; e?: number; lo?: number; hi?: number }>();
    for (const p of actual) m.set(p.d, { d: p.d, v: p.v });
    for (const p of forecast) m.set(p.d, { ...(m.get(p.d) ?? { d: p.d }), e: p.expected, lo: p.low, hi: p.high });
    return [...m.values()].sort((a, b) => a.d.localeCompare(b.d));
  }, [actual, forecast]);
  if (!pts.length) return null;

  const t0 = ms(pts[0].d), t1 = Math.max(ms(pts[pts.length - 1].d), t0 + 86400000);
  const x = (d: string) => pad.l + ((w - pad.l - pad.r) * (ms(d) - t0)) / (t1 - t0);
  const vals = pts.flatMap((p) => [p.v, p.lo, p.hi, p.e].filter((v): v is number => v != null));
  const s = niceScale(Math.min(0, ...vals), Math.max(...vals));
  const y = (v: number) => pad.t + (height - pad.t - pad.b) * (1 - (v - s.lo) / (s.hi - s.lo));
  const line = (xs: { d: string; v: number }[]) => xs.map((p, i) => `${i ? 'L' : 'M'}${x(p.d).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const act = line(actual);
  const exp = line(forecast.map((p) => ({ d: p.d, v: p.expected })));
  const band = forecast.length ? `${line(forecast.map((p) => ({ d: p.d, v: p.high })))} ${forecast.slice().reverse().map((p) => `L${x(p.d).toFixed(1)},${y(p.low).toFixed(1)}`).join(' ')} Z` : '';
  const base = y(Math.max(0, s.lo));
  const area = actual.length ? `${act} L${x(actual[actual.length - 1].d)},${base} L${x(actual[0].d)},${base} Z` : '';

  // Month ticks, thinned so labels never collide (~64px each).
  const months: string[] = [];
  for (const p of pts) if (!months.length || months[months.length - 1] !== p.d.slice(0, 7)) months.push(p.d.slice(0, 7));
  const every = Math.max(1, Math.ceil((months.length * 64) / (w - pad.l - pad.r)));
  const ticks = months.map((m) => `${m}-01`).filter((d, i) => ms(d) >= t0 && i % every === 0);

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const px = e.clientX - e.currentTarget.getBoundingClientRect().left;
    let best = 0, bd = Infinity;
    pts.forEach((p, i) => { const dd = Math.abs(x(p.d) - px); if (dd < bd) { bd = dd; best = i; } });
    setHover(best);
  };
  const hp = hover != null ? pts[hover] : null;
  const hy = hp ? y(hp.v ?? hp.e ?? 0) : 0;

  return (
    <div className="stack">
      <div className="chart-bar">
        <Legend items={[
          { label: 'Kontostand', swatch: 'line', color: 'var(--series-1)' },
          ...(forecast.length ? [{ label: 'Prognose erwartet', swatch: 'dash' as const, color: 'var(--series-1)' }, { label: 'Bandbreite pessimistisch bis optimistisch', swatch: 'band' as const, color: 'var(--series-1)' }] : []),
        ]} />
        <ViewToggle table={table} setTable={setTable} />
      </div>
      {table ? (
        <div className="table-wrap chart-table">
          <table className="data compact" data-copy-surface>
            <thead><tr><th>Datum</th><th className="r">Kontostand</th><th className="r">Erwartet</th><th className="r">Pessimistisch</th><th className="r">Optimistisch</th></tr></thead>
            <tbody>{pts.filter((_, i) => i % 7 === 0 || i === pts.length - 1).map((p) => (
              <tr key={p.d}><td>{date(p.d)}</td><td className="r num">{p.v != null ? chf(p.v, { currency: false }) : '—'}</td><td className="r num">{p.e != null ? chf(p.e, { currency: false }) : '—'}</td><td className="r num">{p.lo != null ? chf(p.lo, { currency: false }) : '—'}</td><td className="r num">{p.hi != null ? chf(p.hi, { currency: false }) : '—'}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <div className="chart" ref={ref}>
          <svg width={w} height={height} role="img" aria-label="Flüssige Mittel: Verlauf und Prognose" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
            <defs>
              <linearGradient id="cash-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--series-1)" stopOpacity="0.16" /><stop offset="1" stopColor="var(--series-1)" stopOpacity="0" /></linearGradient>
            </defs>
            {s.lo < 0 && <><rect x={pad.l} y={y(0)} width={w - pad.l - pad.r} height={y(s.lo) - y(0)} className="deficit" /><text x={pad.l + 8} y={y(s.lo) - 8} className="deficit-label">Unterdeckung</text></>}
            <g className="grid">{s.ticks.map((t) => <line key={t} x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} />)}</g>
            <g className="axis">
              {s.ticks.map((t) => <text key={t} x={pad.l - 8} y={y(t) + 4} textAnchor="end">{kchf(t)}</text>)}
              {ticks.map((d) => <text key={d} x={x(d)} y={height - 8} textAnchor="middle">{monthName(d.slice(0, 7))}{d.slice(5, 7) === '01' ? ` ${d.slice(2, 4)}` : ''}</text>)}
            </g>
            {area && <path d={area} fill="url(#cash-fill)" />}
            {band && <path d={band} className="band" />}
            <line className="baseline" x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} />
            {forecast.length > 0 && <g className="today"><line x1={x(today)} x2={x(today)} y1={pad.t - 8} y2={height - pad.b} /><text x={x(today) + 6} y={pad.t - 6}>Heute</text></g>}
            <path d={act} className="line" />
            {exp && <path d={exp} className="line dashed" />}
            {minimum && forecast.length > 0 && (
              <g className="marker-label">
                <circle cx={x(minimum.date)} cy={y(minimum.expected)} r="4" className="mark" />
                <text x={x(minimum.date)} y={y(minimum.expected) + 20} textAnchor="middle">Tiefpunkt {kchf(minimum.expected)}</text>
              </g>
            )}
            {hp && (
              <g className="crosshair" pointerEvents="none">
                <line x1={x(hp.d)} x2={x(hp.d)} y1={pad.t} y2={height - pad.b} />
                {hp.v != null && <circle cx={x(hp.d)} cy={y(hp.v)} r="4" className="mark" />}
                {hp.e != null && <circle cx={x(hp.d)} cy={y(hp.e)} r="4" className="mark" />}
              </g>
            )}
          </svg>
          {hp && (
            <Tip x={x(hp.d)} y={hy} w={w}>
              <b>{date(hp.d)}{hp.d === today ? ' · heute' : hp.d > today ? ' · Prognose' : ''}</b>
              {hp.v != null && <Row label="Kontostand" value={chf(hp.v)} color="var(--series-1)" />}
              {hp.e != null && hp.d > today && <Row label="Erwartet" value={chf(hp.e)} color="var(--series-1)" dash />}
              {hp.lo != null && hp.d > today && <Row label="Pessimistisch" value={chf(hp.lo)} />}
              {hp.hi != null && hp.d > today && <Row label="Optimistisch" value={chf(hp.hi)} />}
            </Tip>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Monthly columns + prior-year line

export function MonthColumns({ rows, label, priorLabel = 'Vorjahr', height = 240 }: { rows: { m: string; v: number; p?: number | null }[]; label: string; priorLabel?: string; height?: number }) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const pad = { l: 56, r: 12, t: 16, b: 28 };
  const hasPrior = rows.some((r) => r.p != null);
  const vals = rows.flatMap((r) => [r.v, r.p ?? 0]);
  const s = niceScale(Math.min(0, ...vals), Math.max(0, ...vals));
  const y = (v: number) => pad.t + (height - pad.t - pad.b) * (1 - (v - s.lo) / (s.hi - s.lo));
  const band = (w - pad.l - pad.r) / Math.max(1, rows.length);
  const bw = Math.max(4, Math.min(24, band * 0.5));
  const cx = (i: number) => pad.l + band * i + band / 2;
  const prior = rows.map((r, i) => (r.p == null ? null : `${cx(i).toFixed(1)},${y(r.p).toFixed(1)}`));
  const priorPath = prior.reduce<string>((acc, p, i) => (p == null ? acc : `${acc}${acc && prior[i - 1] != null ? ' L' : ' M'}${p}`), '');
  const hr = hover != null ? rows[hover] : null;

  return (
    <div className="stack">
      <div className="chart-bar">
        <Legend items={[{ label, swatch: 'bar', color: 'var(--series-1)' }, ...(hasPrior ? [{ label: priorLabel, swatch: 'line' as const, color: 'var(--prior)' }] : [])]} />
        <ViewToggle table={table} setTable={setTable} />
      </div>
      {table ? (
        <div className="table-wrap chart-table">
          <table className="data compact" data-copy-surface>
            <thead><tr><th>Monat</th><th className="r">{label}</th>{hasPrior && <><th className="r">{priorLabel}</th><th className="r">Veränderung</th></>}</tr></thead>
            <tbody>{rows.map((r) => <tr key={r.m}><td>{monthName(r.m)} {r.m.slice(0, 4)}</td><td className={`r num ${r.v < 0 ? 'neg' : ''}`}>{chf(r.v, { currency: false })}</td>{hasPrior && <><td className="r num">{r.p != null ? chf(r.p, { currency: false }) : '—'}</td><td className="r num">{r.p != null ? chf(r.v - r.p, { currency: false, sign: true }) : '—'}</td></>}</tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div className="chart" ref={ref} onPointerLeave={() => setHover(null)}>
          <svg width={w} height={height} role="img" aria-label={`${label} pro Monat${hasPrior ? `, mit ${priorLabel}` : ''}`}>
            <g className="grid">{s.ticks.map((t) => <line key={t} x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} />)}</g>
            <g className="axis">
              {s.ticks.map((t) => <text key={t} x={pad.l - 8} y={y(t) + 4} textAnchor="end">{kchf(t)}</text>)}
              {rows.map((r, i) => (band >= 30 || i % 2 === 0) && <text key={r.m} x={cx(i)} y={height - 8} textAnchor="middle">{monthName(r.m)}</text>)}
            </g>
            {rows.map((r, i) => (
              <g key={r.m} onPointerEnter={() => setHover(i)}>
                <rect x={pad.l + band * i} y={pad.t} width={band} height={height - pad.t - pad.b} fill="transparent" />
                <path d={colPath(cx(i) - bw / 2, bw, y(0), y(r.v))} className={`bar ${hover != null && hover !== i ? 'dim' : ''}`} fill="var(--series-1)" pointerEvents="none" />
              </g>
            ))}
            <line className="baseline" x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} />
            {hasPrior && <path d={priorPath} className="line prior" pointerEvents="none" />}
            {hasPrior && rows.map((r, i) => r.p != null && <circle key={r.m} cx={cx(i)} cy={y(r.p)} r="4" className="mark prior" pointerEvents="none" />)}
          </svg>
          {hr && hover != null && (
            <Tip x={cx(hover)} y={y(Math.max(hr.v, hr.p ?? hr.v))} w={w}>
              <b>{monthName(hr.m)} {hr.m.slice(0, 4)}</b>
              <Row label={label} value={chf(hr.v)} color="var(--series-1)" />
              {hr.p != null && <Row label={priorLabel} value={chf(hr.p)} color="var(--prior)" />}
              {hr.p != null && <Row label="Veränderung" value={chf(hr.v - hr.p, { sign: true })} />}
            </Tip>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Waterfall

/** From revenue to result: the first step is the start, the others are signed changes, the total is computed. */
export function Waterfall({ steps, totalLabel = 'Ergebnis', upLabel = 'Ertrag', downLabel = 'Aufwand', startLabel, share = true, height = 260 }: { steps: { key: string; label: string; value: number }[]; totalLabel?: string; upLabel?: string; downLabel?: string; startLabel?: string; share?: boolean; height?: number }) {
  const [ref, w] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const pad = { l: 56, r: 12, t: 24, b: 40 };
  const total = steps.reduce((s, x) => s + x.value, 0);
  const bars = useMemo(() => {
    let run = 0;
    const out = steps.map((st, i) => { const from = i === 0 ? 0 : run; run += st.value; return { ...st, from, to: run, kind: i === 0 ? 'start' : st.value >= 0 ? 'up' : 'down' }; });
    out.push({ key: 'total', label: totalLabel, value: total, from: 0, to: total, kind: 'total' });
    return out;
  }, [steps, total, totalLabel]);
  const s = niceScale(Math.min(0, ...bars.map((b) => Math.min(b.from, b.to))), Math.max(0, ...bars.map((b) => Math.max(b.from, b.to))));
  const y = (v: number) => pad.t + (height - pad.t - pad.b) * (1 - (v - s.lo) / (s.hi - s.lo));
  const band = (w - pad.l - pad.r) / bars.length;
  const bw = Math.min(40, band * 0.6);
  const cx = (i: number) => pad.l + band * i + band / 2;
  const fill = (k: string, v = 0) => (k === 'down' || (k === 'start' && !startLabel && v < 0) ? 'var(--series-2)' : k === 'total' || (k === 'start' && startLabel) ? 'var(--total)' : 'var(--series-1)');
  const rect = (b: (typeof bars)[number], i: number) => {
    const top = y(Math.max(b.from, b.to)), bot = y(Math.min(b.from, b.to));
    if (b.kind === 'start' || b.kind === 'total') return colPath(cx(i) - bw / 2, bw, y(0), y(b.to));
    const h = Math.max(1, bot - top), r = Math.min(3, h / 2);
    return `M${cx(i) - bw / 2 + r},${top} H${cx(i) + bw / 2 - r} Q${cx(i) + bw / 2},${top} ${cx(i) + bw / 2},${top + r} V${top + h - r} Q${cx(i) + bw / 2},${top + h} ${cx(i) + bw / 2 - r},${top + h} H${cx(i) - bw / 2 + r} Q${cx(i) - bw / 2},${top + h} ${cx(i) - bw / 2},${top + h - r} V${top + r} Q${cx(i) - bw / 2},${top} ${cx(i) - bw / 2 + r},${top} Z`;
  };
  const words = (l: string) => { const p = l.split(' '); return p.length > 1 && l.length > 10 ? [p.slice(0, Math.ceil(p.length / 2)).join(' '), p.slice(Math.ceil(p.length / 2)).join(' ')] : [l]; };
  const hb = hover != null ? bars[hover] : null;

  return (
    <div className="stack">
      <div className="chart-bar">
        <Legend items={[...(startLabel ? [{ label: startLabel, swatch: 'bar' as const, color: 'var(--total)' }] : []), { label: upLabel, swatch: 'bar', color: 'var(--series-1)' }, { label: downLabel, swatch: 'bar', color: 'var(--series-2)' }, { label: totalLabel, swatch: 'bar', color: 'var(--total)' }]} />
        <ViewToggle table={table} setTable={setTable} />
      </div>
      {table ? (
        <div className="table-wrap chart-table">
          <table className="data compact" data-copy-surface>
            <thead><tr><th>Position</th><th className="r">Betrag</th><th className="r">Kumuliert</th></tr></thead>
            <tbody>{bars.map((b) => <tr key={b.key} className={b.kind === 'total' ? 'total' : ''}><td>{b.label}</td><td className={`r num ${b.value < 0 ? 'neg' : ''}`}>{chf(b.value, { currency: false })}</td><td className="r num">{chf(b.to, { currency: false })}</td></tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div className="chart" ref={ref} onPointerLeave={() => setHover(null)}>
          <svg width={w} height={height} role="img" aria-label={`Vom Umsatz zum ${totalLabel}`}>
            <g className="grid">{s.ticks.map((t) => <line key={t} x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} />)}</g>
            <g className="axis">
              {s.ticks.map((t) => <text key={t} x={pad.l - 8} y={y(t) + 4} textAnchor="end">{kchf(t)}</text>)}
              {bars.map((b, i) => <text key={b.key} x={cx(i)} y={height - pad.b + 16} textAnchor="middle">{words(b.label).map((l, j) => <tspan key={j} x={cx(i)} dy={j ? 13 : 0}>{l}</tspan>)}</text>)}
            </g>
            {bars.slice(0, -1).map((b, i) => <line key={b.key} className="connector" x1={cx(i) + bw / 2} x2={cx(i + 1) - bw / 2} y1={y(b.to)} y2={y(b.to)} />)}
            {bars.map((b, i) => (
              <g key={b.key} onPointerEnter={() => setHover(i)}>
                <rect x={pad.l + band * i} y={pad.t} width={band} height={height - pad.t - pad.b} fill="transparent" />
                <path d={rect(b, i)} fill={fill(b.kind, b.value)} className={`bar ${hover != null && hover !== i ? 'dim' : ''}`} pointerEvents="none" />
                <text className="value-label" x={cx(i)} y={b.kind === 'down' ? y(Math.min(b.from, b.to)) + 14 : y(Math.max(b.from, b.to)) - 6} textAnchor="middle">{kchf(b.value)}</text>
              </g>
            ))}
            <line className="baseline" x1={pad.l} x2={w - pad.r} y1={y(0)} y2={y(0)} />
          </svg>
          {hb && hover != null && (
            <Tip x={cx(hover)} y={y(Math.max(hb.from, hb.to))} w={w}>
              <b>{hb.label}</b>
              <Row label="Betrag" value={chf(hb.value)} color={fill(hb.kind, hb.value)} />
              {share && hb.kind !== 'start' && hb.kind !== 'total' && steps[0]?.value ? <Row label="in % des Umsatzes" value={`${((Math.abs(hb.value) / steps[0].value) * 100).toFixed(1)} %`} /> : null}
              {share && hb.kind === 'total' && steps[0]?.value ? <Row label="Marge" value={`${((hb.value / steps[0].value) * 100).toFixed(1)} %`} /> : null}
            </Tip>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Horizontal bars with a prior-period tick

export interface HBar { key: string; label: string; sub?: string; value: number; prior?: number | null; right?: ReactNode; flag?: ReactNode; onClick?: () => void }

export function HBars({ rows, priorLabel = 'Vorjahr', valueLabel = 'Aktuell' }: { rows: HBar[]; priorLabel?: string; valueLabel?: string }) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(1, ...rows.flatMap((r) => [r.value, r.prior ?? 0]));
  const hasPrior = rows.some((r) => r.prior != null);
  return (
    <div className="stack-sm">
      {hasPrior && <Legend items={[{ label: valueLabel, swatch: 'bar', color: 'var(--series-1)' }, { label: priorLabel, swatch: 'tick', color: 'var(--prior)' }]} />}
      <div className="hbars" data-copy-surface onPointerLeave={() => setHover(null)}>
        {rows.map((r) => {
          const Tag = r.onClick ? 'button' : 'div';
          return (
            <Tag key={r.key} className={`hbar ${hover && hover !== r.key ? 'dim' : ''}`} data-copy-unit onPointerEnter={() => setHover(r.key)} onClick={r.onClick} type={r.onClick ? 'button' : undefined}>
              <span className="hbar-label"><b>{r.label}{r.flag}</b>{r.sub && <span>{r.sub}</span>}</span>
              <span className="hbar-track" aria-hidden>
                <i style={{ width: `${(Math.max(0, r.value) / max) * 100}%` }} />
                {r.prior != null && r.prior > 0 && <u style={{ left: `calc(${(r.prior / max) * 100}% - 1px)` }} />}
              </span>
              <span className="hbar-val num">{chf(r.value, { currency: false })}{r.right && <span>{r.right}</span>}</span>
              {hover === r.key && r.prior != null && (
                <span className="tooltip hbar-tip" role="tooltip">
                  <b>{r.label}</b>
                  <span className="tip-row"><span>{valueLabel}</span><b className="num">{chf(r.value)}</b></span>
                  <span className="tip-row"><span>{priorLabel}</span><b className="num">{chf(r.prior)}</b></span>
                  <span className="tip-row"><span>Veränderung</span><b className="num">{chf(r.value - r.prior, { sign: true })}</b></span>
                </span>
              )}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Aging: one stacked bar, sequential by age

export function AgingBar({ buckets, label }: { buckets: { key: string; label: string; amount: number; count: number }[]; label: string }) {
  const [hover, setHover] = useState<string | null>(null);
  const total = buckets.reduce((s, b) => s + b.amount, 0);
  const shade = (i: number) => (i === 0 ? 'var(--aging-0)' : `var(--aging-${i})`);
  if (!total) return <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>Keine offenen Posten.</p>;
  return (
    <div className="aging" onPointerLeave={() => setHover(null)}>
      <div className="aging-bar" role="img" aria-label={`${label}: ${buckets.filter((b) => b.amount).map((b) => `${b.label} ${chf(b.amount)}`).join(', ')}`}>
        {buckets.map((b, i) => b.amount > 0 && (
          <span key={b.key} style={{ flexGrow: b.amount, background: shade(i) }} className={hover && hover !== b.key ? 'dim' : ''} onPointerEnter={() => setHover(b.key)}>
            {hover === b.key && <span className="tooltip aging-tip"><b>{b.label}</b><span className="tip-row"><span>{b.count} {b.count === 1 ? 'Posten' : 'Posten'}</span><b className="num">{chf(b.amount)}</b></span><span className="tip-row"><span>Anteil</span><b className="num">{Math.round((b.amount / total) * 100)} %</b></span></span>}
          </span>
        ))}
      </div>
      <div className="aging-legend" data-copy-surface>
        {buckets.map((b, i) => (
          <div key={b.key} data-copy-unit className={b.amount ? '' : 'dim'} onPointerEnter={() => setHover(b.key)}>
            <span className="sw" style={{ background: shade(i) }} aria-hidden />
            <span>{b.label}</span>
            <b className="num">{b.amount ? chf(b.amount, { currency: false }) : '—'}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Mini forecast (Cockpit)

/** 90-day outlook in a small space: expected line, scenario band, zero line if in reach, hover readout. */
export function MiniForecast({ points, height = 72 }: { points: ForecastPoint[]; height?: number }) {
  const [ref, w] = useWidth(240);
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return null;
  const lo = Math.min(0, ...points.map((p) => p.low)), hi = Math.max(...points.map((p) => p.high));
  const x = (i: number) => 4 + (i * (w - 8)) / (points.length - 1);
  const y = (v: number) => 6 + (height - 12) * (1 - (v - lo) / (hi - lo || 1));
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.expected).toFixed(1)}`).join(' ');
  const band = `${points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.high).toFixed(1)}`).join(' ')} ${points.map((p, i) => [i, p] as const).reverse().map(([i, p]) => `L${x(i).toFixed(1)},${y(p.low).toFixed(1)}`).join(' ')} Z`;
  const hp = hover != null ? points[hover] : null;
  return (
    <div className="chart" ref={ref} onPointerLeave={() => setHover(null)}>
      <svg width={w} height={height} role="img" aria-label={`Liquiditätsprognose bis ${date(points[points.length - 1].d)}: erwartet ${chf(points[points.length - 1].expected)}`}
        onPointerMove={(e) => { const px = e.clientX - e.currentTarget.getBoundingClientRect().left; setHover(Math.max(0, Math.min(points.length - 1, Math.round(((px - 4) / (w - 8)) * (points.length - 1))))); }}>
        {lo < 0 && <rect x="0" y={y(0)} width={w} height={height - y(0)} className="deficit" />}
        <path d={band} className="band" />
        {lo < 0 && <line x1="0" x2={w} y1={y(0)} y2={y(0)} className="baseline" />}
        <path d={line} className="line" />
        {hp && hover != null && <g className="crosshair"><line x1={x(hover)} x2={x(hover)} y1="0" y2={height} /><circle cx={x(hover)} cy={y(hp.expected)} r="4" className="mark" /></g>}
      </svg>
      {hp && hover != null && (
        <Tip x={x(hover)} y={y(hp.expected)} w={w}>
          <b>{date(hp.d)}</b>
          <Row label="Erwartet" value={chf(hp.expected)} color="var(--series-1)" />
          <Row label="Bandbreite" value={`${kchf(hp.low)} bis ${kchf(hp.high)}`} />
        </Tip>
      )}
    </div>
  );
}
